import { useQuery } from '@tanstack/react-query';
import { apiClient } from 'api';
import { useToast } from 'hooks/useToast';
import { useEffect, useRef } from 'react';

export interface PersonalityBoundary {
  name: string;
  score: number;
  score_range: string;
  description: string;
  implications: string[];
}

export interface PersonalityCoreArea {
  title: string;
  top_boundary: PersonalityBoundary;
  bottom_boundary: PersonalityBoundary;
}

interface StrengthOrGrowthArea {
  title: string;
  content: string;
}

export interface PersonalityEmployerExpectation {
  title: string;
  name: string;
  score: number;
  candidate_score: number;
}

interface EmployerPreference {
  talent_archetype: string;
  personality_type: string;
  strengths: StrengthOrGrowthArea[];
  growth_areas: StrengthOrGrowthArea[];
}

export interface PersonalityData {
  score: number;
  core_areas: PersonalityCoreArea[];
  personality_type: string;
  personality_code: string;
  talent_archetype: string;
  description: string;
  strengths: StrengthOrGrowthArea[];
  growth_areas: StrengthOrGrowthArea[];
  employer_expectations: PersonalityEmployerExpectation[];
  employer_preference: EmployerPreference;
  version: number;
}

interface PersonalityResponse {
  message: string;
  data: PersonalityData;
}

/**
 * Hook for fetching personality data
 */
export const usePersonality = (reportId?: string) => {
  const { openToast } = useToast();
  const hasShownError = useRef(false);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['personality', reportId],
    queryFn: async () => {
      if (!reportId) {
        throw new Error('Report ID is required');
      }

      const response = await apiClient.get<PersonalityResponse>(
        `/reports/${reportId}/personality/`
      );
      return response.data.data;
    },
    enabled: !!reportId,
    retry: 1,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (error && !hasShownError.current) {
      hasShownError.current = true;
      const errorMessage = (error as any).response?.data?.message || (error as any).message || 'Failed to fetch personality';
      openToast({
        show: true,
        heading: "Error",
        text: errorMessage,
        type: "error",
        timeOut: 5000,
      });
    }
    
    if (!error) {
      hasShownError.current = false;
    }
  }, [error, openToast]);

  return {
    personality: data,
    isLoading,
    error,
    refetch,
  };
};
